import type React from 'react';

import { Grid } from '@change/ds-layout';

import { ClientRender } from '@change-corgi/core-react/ssr/render';
import { TrackingContext } from '@change-corgi/core-react/tracking';

import { SetPageStateValue } from '#src/shared/pageState';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { MobileSignFormContextProvider } from '#src/app/pages/petitionDetails/shared/components';
import {
	usePetitionPageStateData,
	useTrackPageView,
	useShowNewlyCreatedPetitions,
	useShowSuggestedVictoriousPetitions,
	useTrackPromotedView,
	PageScrollPositionProvider,
} from '#src/app/pages/petitionDetails/shared/hooks';
import { BeforeSuggestedPetitionsWaypoint } from '#src/app/pages/petitionDetails/shared/waypoints';
import { AppDownloadBottomBannerV2 } from '#src/app/shared/components/banners/appDownloadV2';
import { PetitionUnderReviewBanner } from '#src/app/shared/components/banners/petitionUnderReview';
import { MembershipBottomBanner } from '#src/app/shared/components/membershipBottomBanner';
import { SuggestedPetitionsLazy } from '#src/app/shared/components/suggestedPetitionsLazy';

import { DmFeedBannerContainer } from './DmFeedBanner';
import { ExpiredSessionBanner } from './ExpiredSessionBanner';
import { HeadingContainer } from './Heading';
import { MainContainer } from './Main';
import { useVideoFirstExperiment } from './Main/hooks/useVideoFirstExperiment';
import { NativeShareMobile } from './NativeShareMobile';
import { RedirectUnpublished } from './RedirectUnpublished';
import { ScrollTrackingWaypointContainer } from './ScrollTrackingWaypoint';
import { StrengthTips } from './strengthTips/StrengthTips';

export const DetailsContainer: React.FC = () => {
	useTrackPageView();
	useTrackPromotedView();
	const showSuggestedVictoriousPetitions = useShowSuggestedVictoriousPetitions();
	const showNewlyCreated = useShowNewlyCreatedPetitions();
	const {
		data: { petition, iOSBannerV2Enabled, androidBannerV2Enabled, desktopBannerV2Enabled },
	} = usePetitionDetailsPageData();
	const { photo } = petition;
	const petitionPageStateData = usePetitionPageStateData();
	const {
		data: {
			ownPetition,
			experiments: { relatedPetitionCardShareExperiment },
		},
	} = usePetitionDetailsPageUserData();
	const { videoFirstSourceUrl } = useVideoFirstExperiment();
	const underReview = petition.status === 'UNDER_REVIEW';

	return (
		<div data-testid="details-container" className="mb-8">
			<ExpiredSessionBanner />
			{underReview && <PetitionUnderReviewBanner />}
			<RedirectUnpublished />
			{/* FIXME: for consistency this should be rendered in DetailsWithContext with the other providers. */}
			<PageScrollPositionProvider>
				<SetPageStateValue name="petition" value={petitionPageStateData} />
				<DmFeedBannerContainer />
				<StrengthTips />
				{/* NativeShareMobile is client-only because it checks the user-agent, which is not available in SSR */}
				<ClientRender>
					<NativeShareMobile />
				</ClientRender>

				<Grid className="lg:!col-gap-6 lg:!row-gap-0 relative gap-0! px-0 lg:max-w-[1376px] lg:grid-cols-14!">
					<div className="hidden md:col-start-1 md:col-end-12 md:block lg:col-start-2 lg:col-end-14">
						{photo === null && !videoFirstSourceUrl ? <HeadingContainer /> : null}
					</div>
					<div className="col-span-full">
						<MobileSignFormContextProvider>
							<MainContainer />
						</MobileSignFormContextProvider>
					</div>
				</Grid>
				<BeforeSuggestedPetitionsWaypoint />
				<ScrollTrackingWaypointContainer name="scroll_other_petitions_visible" />
				<SuggestedPetitionsLazy
					showVictorious={showSuggestedVictoriousPetitions}
					showNewlyCreated={showNewlyCreated}
					petitionSlugOrId={petition.id}
					channel="petition_footer"
					sourceLocation="petition_footer"
					experiment={relatedPetitionCardShareExperiment}
				/>
			</PageScrollPositionProvider>

			{/* MembershipBottomBanner is client-only because it checks the user-agent, which is not available in SSR */}
			<ClientRender>
				<MembershipBottomBanner slug={petition.slug} sourceLocation="petition_membership_popup" />
			</ClientRender>
			<TrackingContext properties={{ petition_id: `petition_${petition.id}` }}>
				<AppDownloadBottomBannerV2
					sourceLocation="petition"
					ownPetition={ownPetition}
					iOSBannerEnabled={iOSBannerV2Enabled}
					androidBannerEnabled={androidBannerV2Enabled}
					desktopBannerEnabled={desktopBannerV2Enabled}
				/>
			</TrackingContext>
		</div>
	);
};
